import type { IWorkspace } from '../../api/src/workspace';
import { callApi } from '../api';
import { getToken } from '../auth';
import { PRODUCT_NAME } from '../app/product';

export interface IBackgroundInfo {
  /** This deployment has the filing service (our own Functions app is set up). */
  available: boolean;
  /** The filing service's app ID: what a site admin gives write access to. */
  appId?: string;
  status?: { lastRun: string; filed: number; queued: number; problem?: string };
}

export async function getBackground(driveId: string): Promise<IBackgroundInfo> {
  return callApi<IBackgroundInfo>(`background/${encodeURIComponent(driveId)}`);
}

/**
 * Giving a site's access to the filing service is a SharePoint admin's call: it needs the admin's
 * own Sites.FullControl.All, asked for only here and only from them (not at sign-in).
 */
const GRANT_SCOPE: string[] = ['https://graph.microsoft.com/Sites.FullControl.All'];

/** Asks for the admin permission now, from the click (Microsoft's page, then back to this screen). */
export async function askAdminPermission(): Promise<void> {
  await getToken(GRANT_SCOPE, true, true);
}

async function graphAsAdmin(path: string, method: string, body?: unknown): Promise<unknown> {
  const token: string = await getToken(GRANT_SCOPE, true, true);
  const r: Response = await fetch(`https://graph.microsoft.com/v1.0${path}`, {
    method,
    headers: { Authorization: `Bearer ${token}`, ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) },
    body: body === undefined ? undefined : JSON.stringify(body)
  });
  if (r.status === 403 || r.status === 401) {
    throw new Error(`Only a SharePoint or Microsoft 365 admin can give ${PRODUCT_NAME} access to this site. Ask your admin to do this step.`);
  }
  if (!r.ok) {
    throw new Error(`Microsoft answered HTTP ${r.status}. Try again in a moment.`);
  }
  return r.status === 204 ? undefined : r.json();
}

interface ISitePermission {
  id: string;
  grantedToIdentitiesV2?: { application?: { id?: string } }[];
  grantedToIdentities?: { application?: { id?: string } }[];
}

/** Gives the filing service write access to this one site (nothing else in the organisation). */
export async function grantSite(w: IWorkspace, appId: string): Promise<void> {
  await graphAsAdmin(`/sites/${w.siteId}/permissions`, 'POST', {
    roles: ['write'],
    grantedToIdentities: [{ application: { id: appId, displayName: 'AI Admin Filing Service' } }]
  });
}

/** Removes the filing service's access to this site again. */
export async function revokeSite(w: IWorkspace, appId: string): Promise<void> {
  const list = await graphAsAdmin(`/sites/${w.siteId}/permissions`, 'GET') as { value: ISitePermission[] };
  for (const p of list.value) {
    const ids: (string | undefined)[] = [...(p.grantedToIdentitiesV2 ?? []), ...(p.grantedToIdentities ?? [])].map(g => g.application?.id);
    if (ids.indexOf(appId) !== -1) {
      await graphAsAdmin(`/sites/${w.siteId}/permissions/${p.id}`, 'DELETE');
    }
  }
}
